'use client';

import React from 'react';
import {
  LampWallDown,
  MessageSquareText,
  Send,
  Upload,
  Mic,
  Video,
  Users,
  Sparkles,
  Bot,
  Zap,
  HelpCircle,
  Settings,
  Menu,
  BookOpen,
  Bell,
  Coins,
  Flame,
  KeyRound,
  Briefcase,
  MessageCircle,
  PlusCircle
} from 'lucide-react';

// Pre-defined coach image for performance
const COACH_IMAGE = { src: "/assets/images/coaches/5.png", alt: 'Coach Finn' };

// Pre-defined conditions for better performance
const ALL_DEVICES = { type: "device", value: "all" };
const MOBILE_ONLY = { type: "device", value: "mobile" };
const DESKTOP_ONLY = { type: "device", value: "desktop" };

// Tour data with memoized icons to prevent recreation
export const tourStepsData = [
  {
    tour: "chatAppTour",
    steps: [
      {
        isIntroStep: true,
        coachImage: COACH_IMAGE,
        title: "Let's Get Started",
        content: "Since we've met, let me show you around. Just a quick tour — you'll be ready to roll in no time! 🚀",
        showSkip: true,
        showControls: true
      },
      {
        icon: <Sparkles size={24} className="text-purple-500" />,
        title: "Chat Starter",
        content: "Choose how you want to practice — each option tailors AI responses to your sales scenario.",
        selector: "#chat-starter",
        viewportID: "chat-starter-scrollable-viewport",
        showControls: true,
        showSkip: true,
        side: "bottom",
        pointerPadding: 12,
        pointerRadius: 8,
        condition: ALL_DEVICES
      },
      {
        icon: <MessageSquareText size={24} className="text-pink-500" />,
        title: "Input Area",
        content: "Type your thoughts or questions here. Shift + Enter for a new line, Enter to send. Finn's always listening.",
        selector: "#inputButton",
        side: "top",
        showControls: true,
        showSkip: true,
        pointerPadding: 12,
        pointerRadius: 8,
        condition: ALL_DEVICES
      },
      {
        icon: <Users size={24} className="text-blue-500" />,
        title: "Meet the Coaches",
        content: "Explore what each coach can help you with. From data prep to roleplays, they've got your back.",
        selector: "#coach",
        side: "top-left",
        showControls: true,
        showSkip: true,
        pointerPadding: 12,
        pointerRadius: 8,
        condition: ALL_DEVICES
      },
      {
        icon: <Mic size={24} className="text-orange-500" />,
        title: "Voice Input",
        content: "Speak instead of type — perfect for hands-free sessions.",
        selector: "#audio",
        side: "top-right",
        showControls: true,
        showSkip: true,
        pointerPadding: 10,
        pointerRadius: 8,
        condition: DESKTOP_ONLY
      },
      {
        icon: <Video size={24} className="text-red-500" />,
        title: "Video Messages",
        content: "Record short videos to show context or explain ideas to your AI coach.",
        selector: "#video",
        side: "top-right",
        showControls: true,
        showSkip: true,
        pointerPadding: 10,
        pointerRadius: 8,
        condition: DESKTOP_ONLY
      },
      {
        icon: <Send size={24} className="text-cyan-500" />,
        title: "Send Message",
        content: "Click here or press Enter to send your message and get instant help.",
        selector: "#send-button",
        side: "top-right",
        showControls: true,
        showSkip: true,
        pointerPadding: 10,
        pointerRadius: 8,
        condition: DESKTOP_ONLY
      },
      {
        icon: <Settings size={24} className="text-indigo-500" />,
        title: "Settings",
        content: "Customize themes, preferences, and more.",
        selector: "#ui-settings",
        side: "right",
        showControls: true,
        showSkip: true,
        condition: DESKTOP_ONLY
      },
      {
        icon: <MessageCircle size={24} className="text-teal-500" />,
        title: "Conversation Threads",
        content: "Review or continue your previous conversations anytime.",
        selector: "#conversation-threads",
        side: "right",
        showControls: true,
        showSkip: true,
        condition: DESKTOP_ONLY
      },
      {
        icon: <PlusCircle size={24} className="text-emerald-500" />,
        title: "New Chat",
        content: "Start a fresh conversation whenever you need a clean slate. Perfect for switching between different topics or coaching sessions.",
        selector: "#new-chat-button",
        side: "right",
        showControls: true,
        showSkip: true,
        pointerPadding: 12,
        pointerRadius: 8,
        condition: DESKTOP_ONLY
      },
      {
        icon: <Briefcase size={24} className="text-amber-500" />,
        title: "Workspace Selector",
        content: "Switch between teams or projects here.",
        selector: "#workspace-switcher",
        side: "right",
        showControls: true,
        showSkip: true,
        condition: DESKTOP_ONLY
      },
      {
        icon: <Menu size={24} className="text-amber-500" />,
        title: "Profile & Admin",
        content: "Edit your profile or access the admin panel from here.",
        selector: "#user-profile",
        side: "bottom-right",
        showControls: true,
        showSkip: true,
        condition: ALL_DEVICES
      },
      {
        icon: <Flame size={24} className="text-rose-500" />,
        title: "Streak Counter",
        content: "Track your daily progress and build consistency.",
        selector: "#streak-counter",
        side: "bottom",
        showControls: true,
        showSkip: true,
        condition: ALL_DEVICES
      },
      {
        icon: <Bell size={24} className="text-indigo-500" />,
        title: "Notifications",
        content: "Stay informed with updates, feedback, and reminders.",
        selector: "#notification-bell",
        side: "bottom-right",
        showControls: true,
        showSkip: true,
        condition: ALL_DEVICES
      },
      {
        icon: <Coins size={24} className="text-yellow-500" />,
        title: "Points",
        content: "Earn points for practicing. Redeem them for future perks!",
        selector: "#points-section",
        side: "bottom-right",
        showControls: true,
        showSkip: true,
        condition: DESKTOP_ONLY
      },
      {
        icon: <KeyRound size={24} className="text-violet-500" />,
        title: "Tokens",
        content: "Use tokens to unlock roleplays and premium features. 😊",
        selector: "#token-badge",
        side: "bottom-right",
        showControls: true,
        showSkip: true,
        condition: DESKTOP_ONLY
      },
      {
        icon: <BookOpen size={24} className="text-emerald-500" />,
        title: "You're Ready!",
        content: "You now know the essentials. Go ahead and let Dextego level up your sales game! 💼🚀",
        showControls: true,
        showSkip: false,
        condition: ALL_DEVICES
      }
    ]
  }
];

// Create a memoized mapping function
const mobileStepsMap = new Map();
const desktopStepsMap = new Map();

export const getTourSteps = (isMobile = false) => {
  // Use cached result if available
  const cacheKey = isMobile ? 'mobile' : 'desktop';
  const cachedResult = isMobile ? mobileStepsMap.get(cacheKey) : desktopStepsMap.get(cacheKey);

  if (cachedResult) {
    return cachedResult;
  }

  // Process steps only if needed
  const filteredTourSteps = tourStepsData.map(tour => {
    const filteredSteps = tour.steps.filter(step => {
      if (!step.condition) return true;
      if (step.condition.type === 'device') {
        if (step.condition.value === 'all') return true;
        if (step.condition.value === 'mobile' && isMobile) return true;
        if (step.condition.value === 'desktop' && !isMobile) return true;
        return false;
      }
      return true;
    });

    return {
      ...tour,
      steps: filteredSteps
    };
  });

  // Cache the result
  if (isMobile) {
    mobileStepsMap.set(cacheKey, filteredTourSteps);
  } else {
    desktopStepsMap.set(cacheKey, filteredTourSteps);
  }

  return filteredTourSteps;
};